import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useSlotGroupOverrides } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';

function isUsableUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === 'false' || lower === 'true' || lower === 'null' || lower === 'undefined' || lower === 'none' || lower === 'n/a') {
    return false;
  }
  return true;
}

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Schedule A Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule A Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const selfGuidedTourLinkDef: SlotGroupDefinition = {
  label: 'Self Guided Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'custom_self_guided_tour_url',
      suggestedFields: ['custom_self_guided_tour_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Self Guided Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

export function ScheduleATourCallout() {
  const ctaLink = useSlotGroup('scheduleatourcallout.cta_link', ctaLinkDef);
  const selfGuidedTourLink = useSlotGroup('scheduleatourcallout.self_guided_tour_link', selfGuidedTourLinkDef);
  const hasSelfGuidedTour = isUsableUrl(selfGuidedTourLink.url);
  const slotGroupOverrides = useSlotGroupOverrides();

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-8 md:py-12 bg-primary">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={sectionRef} className="reveal-fade-up text-center glow-trace-wrap relative">
          <GlowTrace delay={0.3} />
          <RichTextContent
            path="scheduleatourcallout.title"
            label="Title"
            defaultValue='<p>Ready to See It in Person?</p>'
            as="h2"
            className="font-heading text-3xl sm:text-4xl font-normal text-primary-foreground mb-4 uppercase tracking-wide"
          />

          <RichTextContent
            path="scheduleatourcallout.subtitle"
            label="Subtitle"
            defaultValue='<p>Tour <span data-type="field-token" data-field="property_name"></span> and explore the neighborhood firsthand.</p>'
            as="p"
            className="font-body text-lg text-primary-foreground max-w-2xl mx-auto mb-8"
          />

          <div className="w-16 h-1 mx-auto mb-8 rounded-full bg-primary-foreground/40" />

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <EditableSlotGroup
              groupPath="scheduleatourcallout.cta_link"
              definition={ctaLinkDef}
              override={slotGroupOverrides['scheduleatourcallout.cta_link']}
            >
              <a
                href={ctaLink.url || '/schedule-a-tour'}
                target={ctaLink._target || '_self'}
                className="inline-flex items-center justify-center px-8 py-3 text-sm font-semibold tracking-[0.04em] leading-[1.3] rounded-full bg-white text-[var(--vibe-color-text)] hover:bg-[color-mix(in_srgb,var(--vibe-color-primary)_75%,black)] hover:text-[var(--vibe-color-text-on-primary)] hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(0,0,0,0.2)] transition-all duration-300"
              >
                {ctaLink.label || 'Schedule A Tour'}
              </a>
            </EditableSlotGroup>

            {hasSelfGuidedTour && (
              <EditableSlotGroup
                groupPath="scheduleatourcallout.self_guided_tour_link"
                definition={selfGuidedTourLinkDef}
                override={slotGroupOverrides['scheduleatourcallout.self_guided_tour_link']}
              >
                <a
                  href={selfGuidedTourLink.url!}
                  target={selfGuidedTourLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-3 text-sm font-semibold tracking-widest uppercase rounded-full border-2 border-primary-foreground/50 text-primary-foreground hover:border-primary-foreground hover:bg-primary-foreground/10 hover:-translate-y-0.5 transition-all duration-300"
                >
                  {selfGuidedTourLink.label || 'Self Guided Tour'}
                </a>
              </EditableSlotGroup>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleATourCallout',
  displayName: 'Schedule A Tour Callout',
  tokens: {
    'scheduleatourcallout.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Ready to See It in Person?</p>',
    },
    'scheduleatourcallout.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Tour <span data-type="field-token" data-field="property_name"></span> and explore the neighborhood firsthand.</p>',
    },
  },
  slotGroups: {
    'scheduleatourcallout.cta_link': ctaLinkDef,
    'scheduleatourcallout.self_guided_tour_link': selfGuidedTourLinkDef,
  },
};

(ScheduleATourCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleATourCallout;
